import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Receipt, Plus, Loader2, FileText, Download } from 'lucide-react';
import { Sidebar, Topbar } from './Dashboard';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { auth, invoices, clients, getStoredUser } from '../api';
import { FullscreenLoading } from './Time';
import { currency, fmtDate, StatusPill } from '../components/billing/shared';
import { InvoiceEditor } from '../components/billing/InvoiceEditor';
import { BillingSettings } from '../components/billing/BillingSettings';

const FILTERS = [['all', 'All'], ['draft', 'Draft'], ['outstanding', 'Outstanding'], ['overdue', 'Overdue'], ['paid', 'Paid'], ['cancelled', 'Cancelled']];

function SummaryCard({ label, value, sub, tone, testid }) {
  const toneCls = tone === 'good' ? 'text-emerald-800' : tone === 'warn' ? 'text-amber-700' : 'text-stone-900';
  return (
    <div className="rounded-xl bg-white border border-stone-200 p-5" data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold">{label}</div>
      <div className={`mt-2 text-2xl font-semibold tabular-nums ${toneCls}`}>{value}</div>
      {sub && <div className="text-xs text-stone-500 mt-1">{sub}</div>}
    </div>
  );
}

const ReadyToInvoice = ({ unbilled, onGenerate }) => (
  <div className="rounded-xl bg-white border border-stone-200 overflow-hidden">
    <div className="flex items-center justify-between p-5 border-b border-stone-100">
      <div>
        <div className="font-semibold text-stone-900">Ready to invoice</div>
        <div className="text-xs text-stone-500 mt-0.5">Timer hours and approved timesheet entries not yet billed, priced at each person&apos;s rate.</div>
      </div>
      <Badge variant="outline" className="border-stone-300">{unbilled.length}</Badge>
    </div>
    <div className="divide-y divide-stone-100">
      {unbilled.length === 0 && <div className="p-8 text-center text-sm text-stone-500">No unbilled time. Track time on tasks or approve timesheets to see it here.</div>}
      {unbilled.map((u) => (
        <div key={u.clientId} className="p-4 lg:p-5 flex items-center gap-4 hover:bg-stone-50" data-testid={`unbilled-row-${u.clientId}`}>
          <div className="h-11 w-11 rounded-lg bg-emerald-50 text-emerald-800 grid place-items-center flex-shrink-0"><Receipt size={18}/></div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-stone-900 truncate">{u.clientName}</div>
            <div className="text-xs text-stone-500">{u.hours.toFixed(2)}h · {u.taskCount} line{u.taskCount === 1 ? '' : 's'}{u.staff.length ? ` · ${u.staff.join(', ')}` : ''}</div>
          </div>
          <div className="text-right hidden sm:block text-sm font-semibold text-stone-900 tabular-nums" data-testid={`unbilled-value-${u.clientId}`}>{currency(u.value)}<div className="text-xs font-normal text-stone-500">excl. VAT</div></div>
          <Button onClick={() => onGenerate(u.clientId)} size="sm" className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid={`unbilled-generate-${u.clientId}`}>Create invoice</Button>
        </div>
      ))}
    </div>
  </div>
);

const InvoiceTable = ({ list, onOpen }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid="invoices-table">
      <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
        <tr>{['Number', 'Client', 'Issued', 'Due', 'Total', 'Balance', 'Status', ''].map((h, i) => <th key={h || i} className={`px-5 py-3 font-medium ${i >= 4 && i <= 5 ? 'text-right' : 'text-left'}`}>{h}</th>)}</tr>
      </thead>
      <tbody className="divide-y divide-stone-100">
        {list.length === 0 && <tr><td colSpan={8} className="px-5 py-10 text-center text-stone-500">No invoices here.</td></tr>}
        {list.map((inv) => (
          <tr key={inv.id} className="hover:bg-stone-50 cursor-pointer" onClick={() => onOpen(inv.id)} data-testid={`invoice-row-${inv.id}`}>
            <td className="px-5 py-4 font-medium text-stone-900">{inv.number}</td>
            <td className="px-5 py-4 text-stone-700">{inv.clientName}</td>
            <td className="px-5 py-4 text-stone-500 text-xs">{fmtDate(inv.issueDate || inv.createdAt)}</td>
            <td className={`px-5 py-4 text-xs ${inv.overdue ? 'text-red-700 font-medium' : 'text-stone-500'}`}>{fmtDate(inv.dueDate)}</td>
            <td className="px-5 py-4 text-right tabular-nums text-stone-900">{currency(inv.total)}</td>
            <td className="px-5 py-4 text-right tabular-nums font-semibold text-stone-900">{currency(inv.balance)}</td>
            <td className="px-5 py-4"><StatusPill inv={inv} testid={`invoice-status-${inv.id}`}/></td>
            <td className="px-5 py-4 text-right" onClick={(e) => e.stopPropagation()}>
              <a href={invoices.pdfUrl(inv.id)} target="_blank" rel="noreferrer" title="Download PDF" className="text-stone-500 hover:text-emerald-800 p-1 inline-block" data-testid={`invoice-row-pdf-${inv.id}`}><Download size={15}/></a>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const NewInvoiceDialog = ({ open, onOpenChange, clientList, initialClientId, onCreated, toast }) => {
  const [form, setForm] = useState({ clientId: '', notes: '', includeTime: false });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ clientId: initialClientId || '', notes: '', includeTime: false }); }, [open, initialClientId]);
  const submit = (e) => {
    e.preventDefault();
    setBusy(true);
    invoices.create(form).then((inv) => { toast({ title: 'Draft invoice created', description: `${inv.number} · ${currency(inv.total)}` }); onOpenChange(false); onCreated(inv); })
      .catch((err) => toast({ title: 'Could not create invoice', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' }))
      .finally(() => setBusy(false));
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>New invoice</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Client</Label>
            <Select value={form.clientId} onValueChange={(v) => setForm({ ...form, clientId: v })}>
              <SelectTrigger className="mt-1.5" data-testid="new-invoice-client-select"><SelectValue placeholder="Choose a client"/></SelectTrigger>
              <SelectContent>{clientList.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <p className="text-sm text-stone-600" data-testid="new-invoice-hint">Use this for additional work outside the monthly fee. You&apos;ll add fixed fees and disbursements next. Monthly retainer invoices are raised automatically on the 25th.</p>
          <div><Label htmlFor="ni-notes">Notes (optional)</Label><Textarea id="ni-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" data-testid="new-invoice-notes"/></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy || !form.clientId} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="new-invoice-submit-btn">
              {busy ? <Loader2 size={16} className="mr-2 animate-spin"/> : <FileText size={14} className="mr-2"/>} Create draft
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const matches = (inv, f) => (f === 'all' ? true : f === 'outstanding' ? ['sent', 'partial'].includes(inv.status) : f === 'overdue' ? !!inv.overdue : inv.status === f);

export default function Billing() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState(getStoredUser());
  const [loading, setLoading] = useState(true);
  const [invoiceList, setInvoiceList] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [newClientId, setNewClientId] = useState('');
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchAll = async () => {
    try {
      const [me, inv, cl] = await Promise.all([auth.me().catch(() => null), invoices.list(), clients.list()]);
      if (me) setUser(me);
      setInvoiceList(inv);
      setClientList(cl);
      const target = searchParams.get('open');
      if (target && inv.some((i) => i.id === target)) { setOpenId(target); setSearchParams({}, { replace: true }); }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!localStorage.getItem('ad_token')) { nav('/login'); return; }
    fetchAll();
    // eslint-disable-next-line
  }, []);

  if (loading) return <FullscreenLoading label="Loading billing..."/>;

  const live = invoiceList.filter((i) => !['cancelled', 'draft'].includes(i.status));
  const mrr = clientList.reduce((a, c) => a + (Number(c.monthlyRetainer) || 0), 0);
  const outstanding = live.reduce((a, i) => a + i.balance, 0);
  const overdue = live.filter((i) => i.overdue).reduce((a, i) => a + i.balance, 0);
  const collected = live.reduce((a, i) => a + (i.amountPaid || 0), 0);
  const generate = (cid) => { setNewClientId(cid || ''); setNewOpen(true); };

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <Sidebar activeLabel="Billing"/>
      <div className="flex-1 min-w-0">
        <Topbar user={user} firm={user?.firm}/>
        <main className="p-4 lg:p-8 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-serif font-semibold text-stone-900 flex items-center gap-2"><Receipt size={24} className="text-emerald-800"/> Billing</h1>
              <p className="mt-1 text-stone-600 text-sm">Monthly retainer invoices are raised automatically on the 25th. Add invoices here for extra work and record payments.</p>
            </div>
            <Button onClick={() => generate('')} disabled={clientList.length === 0} className="bg-emerald-800 hover:bg-emerald-900 text-white h-10" data-testid="new-invoice-btn"><Plus size={16} className="mr-2"/> New invoice</Button>
          </div>

          <Tabs defaultValue="invoices">
            <TabsList data-testid="billing-tabs">
              <TabsTrigger value="invoices" data-testid="billing-tab-invoices">Invoices</TabsTrigger>
              <TabsTrigger value="settings" data-testid="billing-tab-settings">Invoice settings</TabsTrigger>
            </TabsList>
            <TabsContent value="invoices" className="mt-6 space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <SummaryCard label="Monthly retainers" value={currency(mrr)} sub="invoiced automatically on the 25th" testid="summary-retainers"/>
                <SummaryCard label="Outstanding" value={currency(outstanding)} tone="warn" testid="summary-outstanding"/>
                <SummaryCard label="Overdue" value={currency(overdue)} tone={overdue ? 'warn' : undefined} testid="summary-overdue"/>
                <SummaryCard label="Collected" value={currency(collected)} tone="good" testid="summary-collected"/>
              </div>
              <div className="rounded-xl bg-white border border-stone-200 overflow-hidden">
                <div className="flex items-center justify-between gap-3 flex-wrap p-5 border-b border-stone-100">
                  <div className="font-semibold text-stone-900">Invoices</div>
                  <div className="inline-flex rounded-lg border border-stone-200 p-1 flex-wrap" data-testid="invoice-filters">
                    {FILTERS.map(([k, l]) => (
                      <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`invoice-filter-${k}`}
                        className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${filter === k ? 'bg-emerald-800 text-white' : 'text-stone-600 hover:text-stone-900'}`}>{l}</button>
                    ))}
                  </div>
                </div>
                <InvoiceTable list={invoiceList.filter((i) => matches(i, filter))} onOpen={setOpenId}/>
              </div>
            </TabsContent>
            <TabsContent value="settings" className="mt-6"><BillingSettings toast={toast} isOwner={user?.firmRole === 'owner'}/></TabsContent>
          </Tabs>
        </main>
      </div>
      <NewInvoiceDialog open={newOpen} onOpenChange={setNewOpen} clientList={clientList} initialClientId={newClientId} toast={toast} onCreated={(inv) => { fetchAll(); setOpenId(inv.id); }}/>
      <InvoiceEditor invoiceId={openId} onOpenChange={(o) => { if (!o) setOpenId(null); }} onChanged={fetchAll} toast={toast}/>
    </div>
  );
}
